import { Reveal } from "@/components/Reveal";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { specialtySlug } from "@/lib/seo-slugs";
import { SPECIALTIES } from "@/data/heallexa";

const SITE = "https://heallexa.com";

/** Same head() shape as specialties.$slug.tsx's specialty branch, canonicalized under /providers. */
export function providerSpecialtyHeadMeta(specialty: string, slug: string) {
  const spec = specialty;
  const title = `${spec}, Specialty Overview & Top Cities | Heallexa`;
  const description = `Learn what ${spec.toLowerCase()} do, common services, related specialties, and find ${spec.toLowerCase()} in top U.S. cities on Heallexa.`;
  const url = `${SITE}/providers/${slug}`;
  const decision = decideIndexability({
    providerCount: 1,
    hasFaq: true,
    hasNearbyCities: true,
    hasRelatedProviders: true,
    internalLinkCount: 30,
  });
  return {
    meta: [
      { title },
      { name: "description", content: description },
      robotsMeta(decision.robots),
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      jsonLdScript(collectionPageSchema({ name: title, description, url })),
      jsonLdScript(
        breadcrumbListSchema([
          { name: "Home", url: "/" },
          { name: "Providers", url: "/browse" },
          { name: spec, url: `/providers/${slug}` },
        ]),
      ),
      jsonLdScript(
        faqPageSchema([
          { q: `What does a ${spec.toLowerCase().replace(/s$/, "")} do?`, a: `A ${spec.toLowerCase().replace(/s$/, "")} provides specialized healthcare services. Confirm scope of practice with the provider.` },
          { q: `How do I find a ${spec.toLowerCase().replace(/s$/, "")} near me?`, a: `Use Heallexa to filter ${spec.toLowerCase()} by city and insurance.` },
          { q: `Do ${spec.toLowerCase()} take my insurance?`, a: `Many ${spec.toLowerCase()} accept major plans like BCBS, Aetna, UnitedHealthcare, Cigna, and Medicare. Always verify with the office.` },
        ]),
      ),
    ],
  };
}

// Rendered as SearchExperience's `belowResults` slot on the specialty page —
// no Navbar/Footer of its own (SearchExperience already provides those).
// Just the SEO content (About/related specialties/FAQ) that used to sit
// alongside a second, now-removed results list of its own.
export function ProviderSpecialtyHub({ specialty }: { specialty: string }) {
  const lower = specialty.toLowerCase();
  const singular = specialty.replace(/s$/, "");

  const relatedSpecialties = SPECIALTIES.filter((s) => s !== specialty).slice(0, 12).map((s) => ({
    label: s,
    href: `/providers/${specialtySlug(s)}`,
  }));

  const faq = [
    { q: `What does a ${singular.toLowerCase()} do?`, a: `${specialty} are healthcare professionals who specialize in their field. They diagnose, treat, and help manage conditions related to their specialty.` },
    { q: `When should I see a ${singular.toLowerCase()}?`, a: `Most people see a ${singular.toLowerCase()} when referred by a primary care doctor or for ongoing specialty care. You can also self-refer in most states.` },
    { q: `Do ${lower} accept insurance?`, a: `Most ${lower} on Heallexa accept major insurance carriers. Filter by insurance to find in-network providers.` },
    { q: `How do I prepare for my first visit?`, a: `Bring your insurance card, a photo ID, a list of medications, and any relevant records or imaging from prior visits.` },
  ];

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-6">
      <Reveal from="up">
        <section className="mb-12 card-surface rounded-md p-6 sm:p-8">
          <h2 className="font-display text-[clamp(1.4rem,2.4vw,1.8rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground mb-3">About {lower}</h2>
          <p className="text-[14px] leading-relaxed text-muted-foreground">
            {specialty} provide specialized care across hospitals, group practices, and independent clinics. When choosing one, patients typically weigh insurance acceptance, location, and patient reviews.
          </p>
        </section>
      </Reveal>

      <Reveal from="up" delay={60}>
        <SeoLinkList heading="Related specialties" links={relatedSpecialties} columns={3} className="mb-10" />
      </Reveal>

      <Reveal from="up" delay={60}>
        <SeoFaq items={faq} className="mb-8" />
      </Reveal>
    </div>
  );
}
